import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import ClientMessageChatBox from './ClientMessageChatBox';
import { Loader } from 'semantic-ui-react';
import { GET_ROOM_MESSAGES_QUERY } from '../../../../graphql/queries/getRoomMessagesByType';
import {
  GetRoomMessagesByType,
  GetRoomMessagesByTypeVariables,
  GetRoomMessagesByType_getRoomMessagesByType,
} from '../../../../graphql/queries/types/GetRoomMessagesByType';
import { useTranslation } from 'react-i18next';
import ErrorPage from '../../../common/ErrorPage';

interface Props {
  roomId: string;
  messageType: string;
}

const ShowMessageButtonComponent = ({ roomId, messageType }: Props) => {
  const { loading, error, data } = useQuery<GetRoomMessagesByType, GetRoomMessagesByTypeVariables>(
    GET_ROOM_MESSAGES_QUERY,
    {
      variables: { roomId, messageType },
    },
  );
  const [roomData, setRoomData] = useState<Array<GetRoomMessagesByType_getRoomMessagesByType> | undefined>(undefined);

  useEffect(() => {
    if (data) {
      const { getRoomMessagesByType } = data;
      const copyRoomData = [...getRoomMessagesByType];

      // Sort messages by the most recent activity (either message creation or latest reply)
      const sortedMessages = copyRoomData.sort((a, b) => {
        // Get the latest activity timestamp for each message
        const getLatestTimestamp = (message) => {
          try {
            // If there are replies, find the most recent reply timestamp
            if (message && message.replies && Array.isArray(message.replies) && message.replies.length > 0) {
              const latestReply = message.replies.reduce((latest, reply) => {
                const replyDate = reply?.message?.createdAt ? new Date(reply.message.createdAt).getTime() : 0;
                return replyDate > latest ? replyDate : latest;
              }, 0);

              // Compare with the message's own timestamp
              const messageDate = message.createdAt ? new Date(message.createdAt).getTime() : 0;
              return Math.max(messageDate, latestReply);
            }

            // If no replies, just use the message timestamp
            return message?.createdAt ? new Date(message.createdAt).getTime() : 0;
          } catch (err) {
            console.error('Error calculating timestamp:', err);
            return 0; // Return 0 as fallback to prevent sorting errors
          }
        };

        // Sort in descending order (newest first)
        return getLatestTimestamp(b) - getLatestTimestamp(a);
      });

      setRoomData(sortedMessages);
    }
  }, [data]);

  const { t } = useTranslation();

  if (loading) {
    return <Loader active>{t('system.loading')}</Loader>;
  }

  if (error || !data) {
    return <ErrorPage />;
  }

  return (
    <div style={{ overflowY: 'auto', height: '80vh' }} className="scrollDiv">
      {roomData && roomData.length > 0 ? (
        roomData.map((message: GetRoomMessagesByType_getRoomMessagesByType) => (
          <ClientMessageChatBox key={message.id} message={message} roomId={roomId} messageType={messageType} />
        ))
      ) : (
        <div>
          {messageType === 'DirectMessage' && (
            <div style={{ padding: '5px 25px' }}>{t('client.communicationCard.noRecord')}</div>
          )}
          {messageType === 'HomeworkSubmission' && (
            <div style={{ padding: '5px 25px' }}>{t('client.communicationCard.noHomework')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShowMessageButtonComponent;
